import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, clearLegacyToken } from "@/lib/api";
import { useI18n } from "@/i18n/I18n";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setLang } = useI18n();
  const qc = useQueryClient();
  // Each /auth/me call (and logout) gets a number; only the latest one may update the session
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    const id = ++seq.current;
    let data = null;
    try {
      ({ data } = await api.get("/auth/me"));
    } catch {
      data = null;
    }
    if (id !== seq.current) return data;
    setMe(data);
    if (data?.user?.language) setLang(data.user.language);
    setLoading(false);
    return data;
  }, [setLang]);

  useEffect(() => {
    // OAuth callback establishes the session first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const signIn = useCallback(async () => {
    clearLegacyToken();
    qc.clear();
    return refresh();
  }, [refresh, qc]);

  const logout = useCallback(async () => {
    seq.current += 1;
    await api.post("/auth/logout").catch(() => {});
    clearLegacyToken();
    qc.clear();
    setMe(null);
    setLoading(false);
  }, [qc]);

  const value = useMemo(() => ({ me, loading, refresh, signIn, logout }), [me, loading, refresh, signIn, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function useWs() {
  const { me } = useAuth();
  const role = me?.user?.role;
  return {
    ws: me?.workspace,
    plan: me?.plan,
    status: me?.status,
    usage: me?.usage || {},
    role,
    features: me?.plan?.features || {},
    canWrite: ["owner", "admin", "member"].includes(role),
    canManage: ["owner", "admin"].includes(role),
  };
}
